<template>
  <div class="page-container">
    <div class="feed-container">
      <div class="create-post-section">
        <h3>¿Qué está pasando?</h3>
        <CreatePost @postCreated="onPostCreated" />
      </div>

      <div class="posts-section">
        <div v-if="isLoading" class="loading">
          <p>Cargando sabiduría divina...</p>
        </div>
        
        <div v-else-if="posts.length === 0" class="no-posts">
          <p>Gogo no tiene información que mostrar. Aporta, puñetas.</p>
        </div>
        
        <div v-else class="posts-list">
          <div v-for="post in mainPosts" :key="post.id" class="feed-post">
            <PostCard :post="post" @postDeleted="onPostDeleted" @commentAdded="onCommentAdded" @openPost="goToPostDetail" />
            <div v-if="getDirectComments(post).length > 0" class="feed-comments">
              <div v-for="(comment) in getVisibleComments(post)" :key="comment.id" class="feed-comment">
                <PostCard :post="comment" :isComment="true" @postDeleted="onPostDeleted" @commentAdded="onCommentAdded" @openPost="goToPostDetail" />
              </div>
              <button v-if="hasMoreComments(post)" @click="loadMoreComments(post)" class="btn-load-more">Cargar más...</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import { postService } from '@/services/apiService';
import { useUserStore } from '@/stores/user';
import CreatePost from './CreatePost.vue';
import PostCard from './PostCard.vue';

export default {
  name: 'AppFeed',
  components: {
    CreatePost,
    PostCard
  },
  setup() {
    const goToPostDetail = (post) => {
      router.push({ name: 'PostDetail', params: { id: post.id } });
    };
    // Estado para controlar cuántos comentarios mostrar por post
    const commentsShown = ref({});

    // Devuelve los comentarios directos de un post
    const getDirectComments = (post) => {
      return posts.value.filter(p => p.postPadreId === post.id);
    };

    // Devuelve los comentarios visibles (máximo 3 por defecto)
    const getVisibleComments = (post) => {
      const all = getDirectComments(post);
      const shown = commentsShown.value[post.id] || 3;
      return all
        .slice()
        .sort((a, b) => (b.likes || 0) - (a.likes || 0))
        .slice(0, shown);
    };

    // ¿Hay más comentarios para mostrar?
    const hasMoreComments = (post) => {
      return getDirectComments(post).length > (commentsShown.value[post.id] || 3);
    };

    // Cargar más comentarios para un post
    const loadMoreComments = (post) => {
      commentsShown.value[post.id] = (commentsShown.value[post.id] || 3) + 3;
    };
    const router = useRouter();
    const posts = ref([]);
    const isLoading = ref(true);
    const errorMessage = ref('');

    const userStore = useUserStore();
    const checkAuthentication = () => {
      // Usar el store para validar sesión
      if (!userStore.checkAuth()) {
        router.push('/login');
        return false;
      }
      return userStore.token || localStorage.getItem('authToken');
    };

    const loadPosts = async () => {
      const token = checkAuthentication();
      if (!token) return;

      try {
        isLoading.value = true;
        errorMessage.value = '';
        const response = await postService.getAllPosts(token);
        // Ordenar posts por fecha de creación (más recientes primero)
        posts.value = response.sort((a, b) => 
          new Date(b.fechaCreacion) - new Date(a.fechaCreacion)
        );
      } catch (error) {
        console.error('Error loading posts:', error);
        errorMessage.value = error.message;
        // No forzar logout manualmente, el apiService ya maneja el refresh/logout
      } finally {
        isLoading.value = false;
      }
    };

    const onPostCreated = (newPost) => {
      posts.value.unshift(newPost); // Agregar al inicio de la lista
    };

    const onPostDeleted = (postId) => {
      posts.value = posts.value.filter(post => post.id !== postId);
    };

    const onCommentAdded = (parentPostId, newComment) => {
      // Actualizar el post padre si está en la lista
      const parentPost = posts.value.find(post => post.id === parentPostId);
      if (parentPost) {
        if (!parentPost.comentarios) {
          parentPost.comentarios = [];
        }
        parentPost.comentarios.push(newComment);
      }
    };

    onMounted(() => {
      loadPosts();
    });

    // Solo posts sin padre en el feed principal
    const mainPosts = computed(() => {
      // Filtrar solo posts que NO tienen postPadreId
      return posts.value.filter(p => !p.postPadreId);
    });

    // Refuerzo: eliminar cualquier post con padre de la vista principal
    // y asegurar que los comentarios solo se muestran como hijos
    // (esto ya lo hace getDirectComments y getVisibleComments)

    return {
      posts,
      isLoading,
      errorMessage,
      onPostCreated,
      onPostDeleted,
      onCommentAdded,
      mainPosts,
      getDirectComments,
      getVisibleComments,
      hasMoreComments,
      loadMoreComments,
      commentsShown,
      goToPostDetail
    };
  }
};
</script>

<style scoped>
/* Comentarios anidados en el feed */
.feed-comments {
  margin-left: 3.5rem;
  margin-top: 0.7rem;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.7rem;
}

.feed-comment {
  width: 96%;
  background: #e5d8fa;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(106,13,173,0.10);
  padding: 1rem 1.3rem;
  margin-bottom: 0.4rem;
  border-left: 5px solid #6a0dad;
}

.btn-load-more {
  margin-left: 1rem;
  background: none;
  color: #6a0dad;
  border: none;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.95rem;
  padding: 0.25rem 0.5rem;
  border-radius: 5px;
  transition: background 0.2s;
}

.btn-load-more:hover {
  background: #ece6fa;
}
.page-container {
  min-height: 100vh;
  padding: 2rem;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
}

.feed-container {
  max-width: 600px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.create-post-section {
  background: white;
  border-radius: 15px;
  padding: 1.5rem;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  border: 1px solid #e1e8ed;
}

.create-post-section h3 {
  margin: 0 0 1rem 0;
  color: #333;
  font-size: 1.2rem;
  font-weight: 600;
}

.posts-section {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.loading {
  text-align: center;
  padding: 2rem;
  background: white;
  border-radius: 15px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.no-posts {
  text-align: center;
  padding: 3rem;
  background: white;
  border-radius: 15px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  color: #666;
}

.posts-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

/* Responsive */
@media (max-width: 768px) {
  .page-container {
    padding: 1rem;
  }
  
  .feed-container {
    max-width: 100%;
  }
  
  .create-post-section {
    padding: 1rem;
  }
}
</style>
